import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useHistoryRowDateDown } from '../api/history'
import { useCreateUser, useDeletePart, useDeleteUser, useGetPartDetail, useGetUserDetail, useUpdatePart, useUpdateUser, useCreatePart, useCreateGroup, useGetGroupDetail, useUpdateGroup, useDeleteGroup, useCreateDevice, useGetDeviceDetail, useDeleteDevice, useUpdateDevice, useLinkDevice } from '../api/main'
import { useAuthCheck, useModalApiError } from '../hook'
import { useAuth } from '../store'
import { theme } from '../styles/theme'
import { Button_Delete, Button_Main } from './Button'
import { Dropdown, Dropdown_Device, SearchDropdown } from './Dropdown'

export const UserCreateForm = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(userCreateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { mutateAsync: createUser, isPending } = useCreateUser({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetUserList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const partChange = (value: number | string ) => {
    paramChange('partId')(value)
    paramChange('groupId')(-1)
  }
  const submit = async () => {
    if (isPending) return
    try {
      await createUser(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(userCreateParam)
    setErrorMsg([])
    onClose()
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''}>
      <Title>사용자 등록</Title>
      <InputItem type={'dropdown'} name={'소속'} kind={'parts'} currentItem={param.partId} setCurrentItem={partChange} />
      <InputItem type={'dropdown'} name={'그룹'} kind={'groups'} partNo={param.partId} currentItem={param.groupId} setCurrentItem={paramChange('groupId')} />
      <InputItem type={'dropdown'} name={'권한'} kind={'role'} currentItem={param.role} setCurrentItem={paramChange('role')} />
      <InputItem type={'input'} name={'아이디'} kind={undefined} currentItem={param.loginId} setCurrentItem={paramChange('loginId')} />
      <InputItem type={'input'} name={'이름'} kind={undefined} currentItem={param.userName} setCurrentItem={paramChange('userName')} />
      <InputItem type={'password'} name={'비밀번호'} kind={undefined} currentItem={param.password} setCurrentItem={paramChange('password')} />
      <InputItem type={'password'} name={'비밀번호 확인'} kind={undefined} currentItem={param.passwordCheck} setCurrentItem={paramChange('passwordCheck')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.userDesc} setCurrentItem={paramChange('userDesc')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '등록'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const UserUpdateForm = ({ data, onClose }: FormUpdateProps) => {
  const queryClient = useQueryClient()
  const setAuth = useAuth((store) => store.setAuth)
  const myId = useAuth((store) => store.Id)
  const { checkAuth } = useAuthCheck()
  const [param, setParam] = useState(userUpdateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { data:userDetail, isSuccess, isFetching } = useGetUserDetail(data,{enabled: data !== ''}) // enable 로 빈문자열일때는 요청안하도록
  const { mutateAsync: updateUser, isPending } = useUpdateUser({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetUserList'] }) })
  const { mutateAsync: deleteUser, isPending:deletePending } = useDeleteUser({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetUserList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  
  const updateItem = async () => {
    if (isPending) return
    try {
      const { userName, newPassword, newPasswordCheck, userDesc, loginId } = {...param}
      const newParam = { userName, newPassword, newPasswordCheck, userDesc }
      if(newParam.newPassword === '') delete newParam['newPassword']
      if(newParam.newPasswordCheck === '') delete newParam['newPasswordCheck']

      await updateUser({...newParam,loginId})
      if(myId === loginId) setAuth({ username: userName })
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }
  const deleteItem = async () => {
    if (deletePending) return
    try {
      await deleteUser(param.loginId)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(userUpdateParam)
    setErrorMsg([])
    onClose()
  }
 
  useEffect(()=>{
    if(isSuccess) setParam(prev => ({...prev, ...userDetail}))
  },[isSuccess])


  return (
    <Container $isFetching={isFetching} className={isPending || deletePending ? 'notAllowed' : ''}>
      <Title>사용자 수정</Title>
      <InputItem type={'readonly'} name={'소속'} kind={undefined} currentItem={param.partName} setCurrentItem={()=>{}} />
      <InputItem type={'readonly'} name={'그룹'} kind={undefined} currentItem={param.groupName} setCurrentItem={()=>{}} />
      <InputItem type={'readonly'} name={'권한'} kind={undefined} currentItem={param.role} setCurrentItem={()=>{}} />
      <InputItem type={'readonly'} name={'아이디'} kind={undefined} currentItem={param.loginId} setCurrentItem={()=>{}} />
      <InputItem type={'input'} name={'이름'} kind={undefined} currentItem={param.userName} setCurrentItem={paramChange('userName')} />
      <InputItem type={'password'} name={'새 비밀번호'} kind={undefined} currentItem={param.newPassword ? param.newPassword : ''} setCurrentItem={paramChange('newPassword')} />
      <InputItem type={'password'} name={'새 비밀번호 확인'} kind={undefined} currentItem={param.newPasswordCheck ? param.newPasswordCheck : ''} setCurrentItem={paramChange('newPasswordCheck')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.userDesc} setCurrentItem={paramChange('userDesc')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={updateItem} $width={'48%'} $isLoading={isPending || deletePending}>
          {isPending ? '로딩중..' : '수정'}
        </Button_Main>
        {/* <Button_Main onClick={deleteItem} $bgColor={theme.colors.red} $width={'32%'} $isLoading={isPending || deletePending}>
          {deletePending ? '로딩중..' : '삭제'}
        </Button_Main> */}
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending || deletePending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const PartCreateForm = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(partCreateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { mutateAsync: createPart, isPending } = useCreatePart({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetPartList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const submit = async () => {
    if (isPending) return
    try {
      await createPart(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(partCreateParam)
    setErrorMsg([])
    onClose()
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''}>
      <Title>소속 등록</Title>
      <InputItem type={'input'} name={'소속'} kind={undefined} currentItem={param.partName} setCurrentItem={paramChange('partName')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.partDesc} setCurrentItem={paramChange('partDesc')} />
      <InputItem type={'checkbox'} name={'개인택시조합'} kind={undefined} currentItem={param.individual} setCurrentItem={paramChange('individual')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '등록'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}


export const PartUpdateForm = ({ data, onClose }: FormUpdateProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(partUpdateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { data:partDetail, isSuccess, isFetching } = useGetPartDetail(data,{enabled: data !== ''}) // enable 로 빈문자열일때는 요청안하도록
  const { mutateAsync: updatePart, isPending } = useUpdatePart({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetPartList'] }) })
  const { mutateAsync: deletePart, isPending:deletePending } = useDeletePart({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetPartList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  
  const updateItem = async () => {
    if (isPending) return
    try {
      await updatePart(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }
  const deleteItem = async () => {
    if (deletePending) return
    try {
      await deletePart(param.partId)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(partUpdateParam)
    setErrorMsg([])
    onClose()
  }
 
  useEffect(()=>{
    if(isSuccess) setParam(prev => ({...prev, ...partDetail,partId:data}))
  },[isSuccess])


  return (
    <Container $isFetching={isFetching} className={isPending || deletePending ? 'notAllowed' : ''}>
      <Title>소속 수정</Title>
      <InputItem type={'input'} name={'소속'} kind={undefined} currentItem={param.partName} setCurrentItem={paramChange('partName')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.partDesc} setCurrentItem={paramChange('partDesc')} />
      <InputItem type={'checkbox'} name={'개인택시조합'} kind={undefined} currentItem={param.individual} setCurrentItem={paramChange('individual')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={updateItem} $width={'48%'} $isLoading={isPending || deletePending}>
          {isPending ? '로딩중..' : '수정'}
        </Button_Main>
        {/* <Button_Main onClick={deleteItem} $bgColor={theme.colors.red} $width={'32%'} $isLoading={isPending || deletePending}>
          {deletePending ? '로딩중..' : '삭제'}
        </Button_Main> */}
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending || deletePending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const GroupCreateForm = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(groupCreateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { mutateAsync: createGroup, isPending } = useCreateGroup({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetGroupList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const submit = async () => {
    if (isPending) return
    try {
      await createGroup(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(groupCreateParam)
    setErrorMsg([])
    onClose()
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''}>
      <Title>그룹 등록</Title>
      <InputItem type={'dropdown'} name={'소속'} kind={'parts'} currentItem={param.partId} setCurrentItem={paramChange('partId')} />
      <InputItem type={'input'} name={'그룹'} kind={undefined} currentItem={param.groupName} setCurrentItem={paramChange('groupName')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.groupDesc} setCurrentItem={paramChange('groupDesc')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '등록'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const GroupUpdateForm = ({ data, onClose }: FormUpdateProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(groupUpdateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { data:groupDetail, isSuccess, isFetching } = useGetGroupDetail(data,{enabled: data !== ''}) // enable 로 빈문자열일때는 요청안하도록
  const { mutateAsync: updateGroup, isPending } = useUpdateGroup({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetGroupList'] }) })
  const { mutateAsync: deleteGroup, isPending:deletePending } = useDeleteGroup({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['useGetGroupList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  
  const updateItem = async () => {
    if (isPending) return
    try {
      const { groupName, groupDesc, groupId } = {...param}
      await updateGroup({ groupName, groupDesc, groupId })
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }
  const deleteItem = async () => {
    if (deletePending) return
    try {
      await deleteGroup(param.groupId)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(groupUpdateParam)
    setErrorMsg([])
    onClose()
  }
 
  useEffect(()=>{
    if(isSuccess) setParam(prev => ({...prev, ...groupDetail,groupId:data}))
  },[isSuccess])


  return (
    <Container $isFetching={isFetching} className={isPending || deletePending ? 'notAllowed' : ''}>
      <Title>그룹 수정</Title>
      <InputItem type={'readonly'} name={'소속'} kind={undefined} currentItem={param.partName} setCurrentItem={()=>{}} />
      <InputItem type={'input'} name={'그룹'} kind={undefined} currentItem={param.groupName} setCurrentItem={paramChange('groupName')} />
      <InputItem type={'textarea'} name={'비고'} kind={undefined} currentItem={param.groupDesc} setCurrentItem={paramChange('groupDesc')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={updateItem} $width={'48%'} $isLoading={isPending || deletePending}>
          {isPending ? '로딩중..' : '수정'}
        </Button_Main>
        {/* <Button_Main onClick={deleteItem} $bgColor={theme.colors.red} $width={'32%'} $isLoading={isPending || deletePending}>
          {deletePending ? '로딩중..' : '삭제'}
        </Button_Main> */}
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending || deletePending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const DeviceCreateForm = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(decviceCreateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { mutateAsync: createDevice, isPending } = useCreateDevice({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['getDeviceList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const partChange = (value: number | string ) => {
    paramChange('partId')(value)
    paramChange('groupId')(-1)
  }
  const submit = async () => {
    if (isPending) return
    try {
      await createDevice(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(decviceCreateParam)
    setErrorMsg([])
    onClose()
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''} >
      <Title>단말 등록</Title>
      <InputItem type={'dropdown'} name={'소속'} kind={'parts'} currentItem={param.partId} setCurrentItem={partChange} />
      <InputItem type={'dropdown'} name={'그룹'} kind={'groups'} partNo={param.partId} currentItem={param.groupId} setCurrentItem={paramChange('groupId')} />
      <InputItem type={'input'} name={'차량번호'} kind={undefined} currentItem={param.driveNumber} setCurrentItem={paramChange('driveNumber')} />
      <InputItem type={'input'} name={'별칭'} kind={undefined} currentItem={param.deviceAlias} setCurrentItem={paramChange('deviceAlias')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '등록'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}


export const DeviceLinkForm = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(decviceLinkParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { mutateAsync: linkDevice, isPending } = useLinkDevice({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['getDeviceList'] }) })
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const submit = async () => {
    if (isPending) return
    try {
      await linkDevice(param)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(decviceLinkParam)
    setErrorMsg([])
    onClose()
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''}>
      <Title>단말 연동</Title>
      <InputItem type={'input'} name={'차량번호'} kind={undefined} currentItem={param.driveNumber} setCurrentItem={paramChange('driveNumber')} />
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '연동'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const DeviceUpdateForm = ({ data, onClose }: FormUpdateProps) => {
  const queryClient = useQueryClient()
  const { checkAuth } = useAuthCheck()
  const [param, setParam] = useState(deviceUpdateParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const { data:deviceDetail, isSuccess, isFetching } = useGetDeviceDetail(data,{enabled: data !== ''}) // enable 로 빈문자열일때는 요청안하도록
  const { mutateAsync: updateDevice, isPending } = useUpdateDevice({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['getDeviceList'] }) })
  const { mutateAsync: deleteDevice, isPending:deletePending } = useDeleteDevice({ onSuccess: (data: unknown) => queryClient.invalidateQueries({ queryKey: ['getDeviceList'] }) })
  const paramChange = (name: string) => (value: string | number | LinkedUsersType[]) => setParam((prev) => ({ ...prev, [name]: value }))
  const partChange = (value: number | string ) => {
    paramChange('partId')(value)
    paramChange('groupId')(-1)
  }
  
  const updateItem = async () => {
    if (isPending) return
    try {
      const { partId, groupId, deviceAlias, deviceId, driveNumber, linkedUsers, backReverse, breakReverse } = {...param}
      const newParam = checkAuth('1000') 
                ? { partId, groupId, deviceAlias, driveNumber, backReverse, breakReverse, deviceId:deviceId.toString(), linkedUserIds:linkedUsers.map((v:LinkedUsersType)=>v.loginId) }
                : checkAuth('0110') ? { partId, groupId, deviceAlias, driveNumber, deviceId:deviceId.toString(), linkedUserIds:linkedUsers.map((v:LinkedUsersType)=>v.loginId) }
                : { deviceAlias, driveNumber, deviceId:deviceId.toString(), linkedUserIds:linkedUsers.map((v:LinkedUsersType)=>v.loginId) }
      await updateDevice(newParam)
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }
  const deleteItem = async () => {
    if (deletePending) return
    try {
      const { deviceId } = {...param}
      await deleteDevice(deviceId.toString())
      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const clearClose = () => {
    setParam(deviceUpdateParam)
    setErrorMsg([])
    onClose()
  }
 
  useEffect(()=>{
    if(isSuccess) setParam(prev => ({...prev, ...deviceDetail, deviceId:data}))
  },[isSuccess])


  const usersToString = (array:LinkedUsersType[]) => array.map((item:LinkedUsersType)=>item.userName).join('\n');

  const deleteLinkedUser = (id:string) => setParam((prev) => ({ ...prev, ['linkedUsers']: prev['linkedUsers'].filter((item:LinkedUsersType)=> id !== item.loginId) }))

  return (
    <Container $isFetching={isFetching} className={isPending || deletePending ? 'notAllowed' : ''}>
      <Title>단말 수정</Title>
      {checkAuth('1110') && param.linkedUsers.length === 0 && <InputItem type={'dropdown'} name={'소속'} kind={'parts'} currentItem={param.partId} setCurrentItem={partChange} />}
      {checkAuth('1110') && param.linkedUsers.length === 0 && <InputItem type={'dropdown'} name={'그룹'} kind={'groups'} partNo={param.partId} currentItem={param.groupId} setCurrentItem={paramChange('groupId')} />}
      {(checkAuth('0001') || param.linkedUsers.length > 0) && <InputItem type={'readonly'} name={'소속'} kind={undefined} currentItem={param.partName} setCurrentItem={()=>{}} />}
      {(checkAuth('0001') || param.linkedUsers.length > 0) && <InputItem type={'readonly'} name={'그룹'} kind={undefined} currentItem={param.groupName} setCurrentItem={()=>{}} />}
      <InputItem type={'readonly'} name={'MAC'} kind={undefined} currentItem={param.macAddress} setCurrentItem={()=>{}} />
      <InputItem type={'input'} name={'차량번호'} kind={undefined} currentItem={param.driveNumber} setCurrentItem={paramChange('driveNumber')} />
      <InputItem type={'input'} name={'별칭'} kind={undefined} currentItem={param.deviceAlias} setCurrentItem={paramChange('deviceAlias')} />
      {checkAuth('1000') && <InputGroup>
        <Label>후진등 반전</Label>
        <Input type="checkbox" checked={param.backReverse === "Y" ? true : false} onChange={(e: React.ChangeEvent<HTMLInputElement>) => paramChange('backReverse')(e.target.checked ? "Y" : "")} />
      </InputGroup>}
      {checkAuth('1000') && <InputGroup>
        <Label>Break 반전</Label>
        <Input type="checkbox" checked={param.breakReverse === "Y" ? true : false} onChange={(e: React.ChangeEvent<HTMLInputElement>) => paramChange('breakReverse')(e.target.checked ? "Y" : "")} />
      </InputGroup>}
      <InputItem type={'arrayBtn'} name={'연동 사용자'} kind={undefined} currentItem={param.linkedUsers} setCurrentItem={deleteLinkedUser} $isLoading={(isPending || deletePending)}/>
      {/* {checkAuth('1111') && <InputItem type={'arrayBtn'} name={'연동 사용자'} kind={undefined} currentItem={param.linkedUsers} setCurrentItem={deleteLinkedUser} />}
      {checkAuth('0001') && <InputItem type={'readonly'} name={'연동 사용자'} kind={undefined} currentItem={usersToString(param.linkedUsers)} setCurrentItem={()=>{}} />} */}
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}

      <ButtonContainer>
        <Button_Main onClick={updateItem} $width={checkAuth('1110') ? '32%' : '48%'} $isLoading={isPending || deletePending}>
          {isPending ? '로딩중..' : '수정'}
        </Button_Main>
        {checkAuth('1110') && <Button_Main onClick={deleteItem} $bgColor={theme.colors.red} $width={'32%'} $isLoading={isPending || deletePending}>
          {deletePending ? '로딩중..' : '삭제'}
        </Button_Main>}
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={checkAuth('1110') ? '32%' : '48%'} $isLoading={isPending || deletePending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}

export const InputItem = ({ type, name, kind, partNo, currentItem, $isLoading, setCurrentItem }: InputItemProps) => {
  const { checkAuth, loginId } = useAuthCheck()
  return type === 'input' ? (
    <InputGroup>
      <Label>{name}</Label>
      <Input type="text" value={currentItem} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentItem(e.target.value)} />
    </InputGroup>
  ) : type === 'dropdown' ? (
    <InputGroup>
      <Label>{name}</Label>
      <Dropdown kind={kind} partNo={partNo} currentItem={currentItem} setCurrentItem={setCurrentItem} />
    </InputGroup>
  ) : type === 'password' ? (
    <InputGroup>
      <Label>{name}</Label>
      <Input type="password" value={currentItem} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentItem(e.target.value)} />
    </InputGroup>
  ) : type === 'textarea' ? (
    <InputGroup>
      <Label>{name}</Label>
      <Textarea rows={3} value={currentItem} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentItem(e.target.value)} />
    </InputGroup>
  ) : type === 'readonly' ? (
    <InputGroup>
      <Label>{name}</Label>
      <P>{currentItem}</P>
    </InputGroup>
  ) : type === 'checkbox' ? (
    <InputGroup>
      <Label>{name}</Label>
      <InputCheckBox type="checkbox" checked={currentItem ==='Y' ? true : false} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentItem(e.target.checked ? 'Y' : 'N')} />
    </InputGroup>
  ) : type === 'searchSelect' ? (
    <InputGroup>
      <Label>{name}</Label>
      <SearchDropdown currentItem={currentItem} setCurrentItem={setCurrentItem} />
    </InputGroup>
  ) : type === 'arrayBtn' ? (
    <InputGroup>
      <Label>{name}</Label>
      <DeleteContainer>
        {currentItem.map((item)=>(
          <DeleteItem key={item.loginId}> 
            <P>{item.loginId}</P> 
            {(checkAuth('1110') || (checkAuth('0001') && item.loginId === loginId)) && <Button_Delete onClick={()=>setCurrentItem(item.loginId)} $isLoading={$isLoading} />} 
          </DeleteItem>))}
      </DeleteContainer>
    </InputGroup>
  ) : null
}

export const SearchInputItem = ({ type, name, kind, partNo, currentItem, setCurrentItem }: InputItemProps) => {
  return type === 'input' ? (
    <SearchInputGroup>
      <SearchLabel>{name}</SearchLabel>
      <Input type="text" value={currentItem} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentItem(e.target.value)} />
    </SearchInputGroup>
  ) : type === 'dropdown' ? (
    <SearchInputGroup>
      <SearchLabel>{name}</SearchLabel>
      <Dropdown kind={kind} partNo={partNo} currentItem={currentItem} setCurrentItem={setCurrentItem} />
    </SearchInputGroup>
  ) : type === 'password' ? (
    <SearchInputGroup>
      <SearchLabel>{name}</SearchLabel>
      <Input type="password" value={currentItem} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentItem(e.target.value)} />
    </SearchInputGroup>
  ) : type === 'textarea' ? (
    <SearchInputGroup>
      <SearchLabel>{name}</SearchLabel>
      <Textarea rows={3} value={currentItem} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentItem(e.target.value)} />
    </SearchInputGroup>
  ) : type === 'readonly' ? (
    <SearchInputGroup>
      <SearchLabel>{name}</SearchLabel>
      <P>{currentItem}</P>
    </SearchInputGroup>
  ) : null
}



export const History_Form = ({ onClose }: FormProps) => {
  const queryClient = useQueryClient()
  const [param, setParam] = useState(historyExcelParam)
  const [ErrorMsg, setErrorMsg] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>()
  const {mutateAsync: downloadHistoryRowDateDown, isPending} = useHistoryRowDateDown()
  const paramChange = (name: string) => (value: string | number) => setParam((prev) => ({ ...prev, [name]: value }))
  const partChange = (value: number | string ) => {
    paramChange('partId')(value)
    paramChange('groupId')(-1)
    paramChange('deviceId')(-1)
  }
  const groupChange = (value: number | string ) => {
    paramChange('groupId')(value)
    paramChange('deviceId')(-1)
  }
  const submit = async () => {
    if (isPending) return
    try {
      if(param.startTimeUnix === -1 || param.endTimeUnix === -1){
        setErrorMessage("날입력은 필수 입니다.")
        return
      }
      if(param.startTimeUnix >= param.endTimeUnix){
        setErrorMessage("시작날짜와 종료날짜를 확인해 주세요")
        return
      }
      setErrorMessage("")
      const newParam = { 
        startTimeUnix: param.startTimeUnix, 
        endTimeUnix: param.endTimeUnix,
        ...(param.partId !== -1 && { partId: param.partId }),
        ...(param.groupId !== -1 && { groupId: param.groupId }),
        ...(param.deviceId !== -1 && { deviceId: param.deviceId }) 
      }

      const arrayBuffer = await downloadHistoryRowDateDown(newParam);
      // Blob 객체로 변환
      const csvBlob = new Blob([arrayBuffer], { type: 'text/csv' });
      const url = window.URL.createObjectURL(csvBlob);
      // 파일 다운로드를 위한 링크 생성
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${chagneDate(param.startTimeUnix)}~${chagneDate(param.endTimeUnix)}.csv`); // 파일 이름 지정
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("체크포인트3 : ")

      clearClose()
    } catch (error: unknown) {
      useModalApiError(error,setErrorMsg)
    }
  }

  const chagneDate = (unixTime:number) => {
    const date = new Date(unixTime * 1000);

    // 한국 시간(KST, UTC+9)으로 변환
    const kstOffset = 9 * 60; // UTC+9를 분 단위로 계산
    const kstDate = new Date(date.getTime() + kstOffset * 60 * 1000);

    // "YYYY-MM-DD HH:MM:SS" 형식으로 변환
    const formattedDate = kstDate.toISOString().slice(0, 19).replace("T", " ");

    return formattedDate
  }

  const clearClose = () => {
    setParam(historyExcelParam)
    setErrorMsg([])
    onClose()
  }

  const setStartDate = (value:any) => {
    try {
      const startDateIns = new Date(`${value}T00:00:00+09:00`)
      const startTimeUnix = Math.floor(startDateIns.getTime() / 1000)
      paramChange('startTimeUnix')(startTimeUnix)
    } catch (error) {
      setErrorMessage("올바른 날짜를 입력하세요")
    }
  }

  const setEndDate = (value:any) => {
    try {
      const endTimeUnixIns = new Date(`${value}T00:00:00+09:00`)
      endTimeUnixIns.setTime(endTimeUnixIns.getTime() + (24 * 60 * 60 * 1000))
      const endTimeUnix = Math.floor(endTimeUnixIns.getTime() / 1000)
      paramChange('endTimeUnix')(endTimeUnix)
    } catch (error) {
      setErrorMessage("올바른 날짜를 입력하세요")
    }
  }

  return (
    <Container className={isPending ? 'notAllowed' : ''}>
      <Title>엑셀 다운로드</Title>
      <InputItem type={'dropdown'} name={'소속'} kind={'parts'} currentItem={param.partId} setCurrentItem={partChange} />
      <InputItem type={'dropdown'} name={'그룹'} kind={'groups'} partNo={param.partId} currentItem={param.groupId} setCurrentItem={groupChange} />
      <InputGroup>
        <Label>{"단말"}</Label>
        <Dropdown_Device  groupId={param.groupId} currentItem={param.deviceId} setCurrentItem={paramChange('deviceId')} />
      </InputGroup>
      <InputGroup>
        <Label>{"시작 시간"}</Label>
        <input style={{ height: '40px'}} type="date" name="startTimeUnix" onChange={(e)=>setStartDate(e.target.value)} />
      </InputGroup>
      <InputGroup>
        <Label>{"종료 시간"}</Label>
        <input style={{ height: '40px'}} type="date" name="endTimeUnix" onChange={(e)=>setEndDate(e.target.value)} />
      </InputGroup>
      
      
      {ErrorMsg.length > 0 && <div>{ErrorMsg.map((msg, index) => (<ErrorMessage key={index}>{msg}</ErrorMessage>))}</div>}
      <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>

      <ButtonContainer>
        <Button_Main onClick={submit} $width={'48%'} $isLoading={isPending}>
          {isPending ? '로딩중..' : '다운로드'}
        </Button_Main>
        <Button_Main onClick={clearClose} $bgColor={theme.colors.blue} $width={'48%'} $isLoading={isPending}>
          취소
        </Button_Main>
      </ButtonContainer>
    </Container>
  )
}



const Container = styled.div<{ $isFetching?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 500px;
  max-height: 70vh;
  padding: 20px;
  border-radius: 8px;
  overflow-y: auto;
  overflow-x: hidden; /* 가로 스크롤바는 숨김 */
  color: ${theme.colors.green};
  gap: 15px;

  /* 로딩 중일 때 모자이크 효과 및 포인트 이벤트 금지 */
  filter: ${({ $isFetching }) => ($isFetching ? 'blur(10px)' : 'none')};
  pointer-events: ${({ $isFetching }) => ($isFetching ? 'none' : 'auto')};
  
  &.notAllowed {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }
`
const Title = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`
const InputGroup = styled.div`
  display: flex;
  gap:10px;
`
const Label = styled.label`
  display: flex;
  width: 90px;
  align-items: center;
`
const SearchInputGroup = styled.div`
  display: flex;
  gap:10px;
  margin-right:5px;
`
const SearchLabel = styled.label`
  display: flex;
  align-items: center;
`
const Input = styled.input`
  flex:1;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid ${theme.colors.blackgray};
  font-size: 14px;
`
const InputCheckBox = styled.input`
  display:flex;
  width: 30px;
  height: 30px;
  padding: 10px;
  border-radius: 25px;
  border: 1px solid ${theme.colors.blackgray};
  cursor: pointer;
`

const P = styled.p`
  display: block; /* 또는 'flex'를 사용하되 필요에 따라 변경 */
  flex:1;
  height: 38px;
  align-items: center;
  color: ${theme.colors.black};
  max-width: 100%;
  min-height: 38px;
  padding:5px 5px;
  
  /* 텍스트가 넘칠 때 ...으로 표시 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* 호버 시 전체 내용 표시 */
  &:hover {
    overflow: visible;
    white-space: normal;
    height: auto; /* 필요에 따라 높이 자동 조정 */
    background-color: ${theme.colors.white}; /* 배경 색상 변경 */
    z-index: 1; /* 다른 요소 위에 표시 */
    position: relative; /* z-index가 적용되기 위해 필요 */
  }
`

const Textarea = styled.textarea`
  flex:1;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid ${theme.colors.blackgray}; 
  font-size: 14px;
  resize: none;
`
const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

export const ErrorMessage = styled.div`
  width: 100%;
  border-radius: 4px;
  color: ${theme.colors.red};
  word-wrap: break-word; /* 긴 단어가 자동으로 줄바꿈되게 설정 */
`
const DeleteContainer = styled.div`
  display:flex;
  flex:1;
  align-items:center;
  flex-wrap: wrap;  /* 자식 요소가 넘치면 다음 줄로 */
  gap:3px;
`
const DeleteItem = styled.div`
  display:flex;
  align-items:center;
`

type InputType = 'input' | 'dropdown' | 'password' | 'textarea' | 'readonly' | 'searchSelect' | 'arrayBtn' | 'checkbox'
type KindType = 'parts' | 'groups' | 'role' | 'roleSearch'

type InputItemParam<T> = {
  name: string
  currentItem: T
  $isLoading?: boolean;
  partNo?: number
}

// 각 타입에 맞는 currentItem 정의
type InputItemProps =
  | (InputItemParam<number | string> & { type: 'dropdown'; kind: KindType; setCurrentItem: (value: number | string) => void }) // "dropdown"에 대한 타입
  | (InputItemParam<LinkedUsersType[]> & { type: 'arrayBtn'; kind: undefined; setCurrentItem: (value: string) => void })
  | (InputItemParam<string> & { type: Exclude<InputType, 'dropdown' | 'arrayBtn'>; kind: undefined; setCurrentItem: (value: string) => void })

type LinkedUsersType = { loginId: string; userName: string}

interface FormProps {
  onClose: () => void
}

interface FormUpdateProps extends FormProps {
  data: string
}

const userCreateParam = {
  partId: -1,
  groupId: -1,
  role: '',
  loginId: '',
  userName: '',
  password: '',
  passwordCheck: '',
  userDesc: ''
}

const historyExcelParam = {
  partId: -1,
  groupId: -1,
  deviceId: -1,
  startTimeUnix: -1,
  endTimeUnix: -1,
}



type UserUpdateParamType = {
  partId: number;
  groupId: number;
  partName:string;
  groupName:string;
  role: string;
  loginId: string;
  userName: string;
  newPassword?: string; 
  newPasswordCheck?: string;
  userDesc: string;
}

const userUpdateParam:UserUpdateParamType = {
  partId: -1,
  groupId: -1,
  partName:'',
  groupName:'',
  role: '',
  loginId: '',
  userName: '',
  newPassword: '',
  newPasswordCheck: '',
  userDesc: ''
}

const partCreateParam = {
  partName: '',
  partDesc: '',
  individual: 'N'
}

const partUpdateParam = {
  partId: '-1',
  partName: '',
  partDesc: '',
  individual: 'N'
}

const groupCreateParam = {
  partId: '-1',
  groupName: '',
  groupDesc: ''
}

const groupUpdateParam = {
  partName:'',
  groupId: '-1',
  groupName: '',
  groupDesc: ''
}

const decviceCreateParam = {
  partId: -1,
  groupId: -1,
  driveNumber: '',
  deviceAlias: ''
}

const decviceLinkParam = {
  driveNumber: ''
}

const deviceUpdateParam = {
  partId: -1,
  groupId: -1,
  deviceId: -1,
  partName: '',
  macAddress: '',
  groupName: '',
  driveNumber: '',
  deviceAlias: '',
  backReverse: '',
  breakReverse: '',
  linkedUsers: []
}